import { Fade, useTheme } from '@mui/material';
import { portalPaths, UrlSegments } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import { MetaTags } from 'src/components/MetaTags/meta-tags';
import { Address, RecordOpenMode, useAddressRecord } from 'src/graphql';
import { useRecordOpenMode } from 'src/graphql/entities/record-open-mode/record-open-mode';
import { EntityNames } from 'src/graphql/types';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { BaseSelectedView } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import { CreateButton } from 'src/pages/CustomViews/custom-views-selected-view/components/create-button';
import { useSelectedEntityParams } from 'src/pages/CustomViews/custom-views-selected-view/hooks/use-selected-entity-params';
import { SiteSidePanel } from 'src/pages/Sites/SidePanel/side-panel';
import { TabType, useCustomView, useGlobalTabs, useOnEsc } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing/use-navigate-route';
import { Platform, useScreenDimensions } from 'src/utils/system';

export const SiteSelectedView = () => {
  const { navigate } = useNavigateRoute();
  const { isMobile } = useScreenDimensions();
  const [selectedRecordId, setSelectedRecordId] = useState<Address['id'] | null>(null);

  const customViewId = useCustomView(EntityNames.Site)?.customViewId;

  const { address: selectedRecord } = useAddressRecord({ addressId: selectedRecordId || '' });

  const {
    record: address,
    recordId: addressId,
    openRecord,
    closeRecord,
    customView,
    viewId,
    selectedCustomViewId,
  } = useSelectedEntityParams({ entityName: EntityNames.Site });

  const selectedAddress = useMemo(
    () => (addressId ? ((address as Address) ?? selectedRecord) : undefined),
    [addressId, address, selectedRecord],
  );

  const theme = useTheme();

  const { openInTab } = useGlobalTabs();

  const onPanelClose = useCallback(() => {
    closeRecord();
    setSelectedRecordId(null);
  }, [closeRecord]);

  useOnEsc(onPanelClose);

  const { currentOpenMode } = useRecordOpenMode();

  const onRecordClick = useCallback(
    (siteId: string, site: Address, event?: React.MouseEvent) => {
      if (Platform.isMacOS ? event?.metaKey : event?.ctrlKey) {
        openInTab({ entity: site, type: TabType.Site }, { customViewId }, false);
        return;
      } else {
        if (!isMobile && currentOpenMode === RecordOpenMode.FullPage) {
          navigate(portalPaths.site.full(siteId), { state: { customViewId } });
        } else {
          openRecord(siteId);
        }
      }
    },
    [currentOpenMode, customViewId, isMobile, navigate, openInTab, openRecord],
  );

  return (
    <MetaTags title={selectedAddress?.fullAddress || customView?.name || 'Sites'}>
      <BaseSelectedView<Address>
        title={selectedCustomViewId === UrlSegments.view.all ? 'All sites' : ''}
        entity={EntityNames.Site}
        viewId={viewId}
        selectedRecord={selectedAddress}
        onRecordClick={onRecordClick}
        onRecordClose={() => {
          closeRecord();
          setSelectedRecordId(null);
        }}
        leftPanelWidth={5000}
        sx={{
          [theme.breakpoints.up(0)]: {
            maxWidth: '100%',
          },
        }}
        slots={{
          rightPanel: !!selectedAddress && (
            <Fade in={!!selectedAddress}>
              <EntityWindowWrapper
                sx={{
                  [theme.breakpoints.up('sm')]: {
                    minWidth: '340px',
                  },
                }}
              >
                <SiteSidePanel onClose={onPanelClose} site={selectedAddress} />
              </EntityWindowWrapper>
            </Fade>
          ),
          createButton: <CreateButton icon="add_home_work" />,
        }}
      />
    </MetaTags>
  );
};
